/**
 * BLRRD Metadata panel — Phase 3.B-ux P9.
 *
 * Read-only viewer for image metadata (camera/lens/exposure/timestamps,
 * format, dimensions, AI-generation metadata if present). Surfaces info
 * that previously lived only in "WxH" in the bottom-left infobar.
 *
 * Mounted lazily via `openMetadataPanel()` (called by toolbar btnInfo).
 * Uses native <dialog> like src/ui/batch-modal.js and hdr-merge-modal.js.
 *
 * Privacy invariants (BLRRD privacy-first claim has to hold):
 *   - GPS coordinates are HIDDEN by default. The user can opt in via a
 *     "Show GPS" button which surfaces a warning + sets
 *     window.E.metadataShowGPS = true for the rest of the session.
 *   - Camera serial / shutter count / owner-name are NEVER shown. These
 *     are PII fingerprinting vectors per docs/THREAT-MODEL.md and are
 *     also stripped on JPEG export (export.stripPii toggle).
 *   - Read-only display only. No EXIF editing in v1.
 *
 * Data sources (layered, first match wins per field):
 *   - window.E.decodedMetadata    — RAW path (libraw struct, populated
 *                                   by loadImageFromDecoded). Carries
 *                                   make/model/lens/iso/shutter/aperture/
 *                                   focal_len/timestamp.
 *   - blob arrayBuffer + piexifjs — JPEG path (and HEIC/HEIF EXIF where
 *                                   available). Reads 0th IFD + ExifIFD
 *                                   + GPSIFD.
 *   - window.E.aiMetadata         — Phase 2.B AI metadata (PNG path).
 *   - window.E.imgData / .w / .h  — fallback dimensions.
 *
 * @module src/ui/metadata-panel
 */

import { t } from '../i18n/index.js';

const DASH = '—'; /* em-dash for missing fields */

let dialog = null;

/* ── Public API ─────────────────────────────────────────────────────── */

/**
 * Build a normalized, sanitized MetadataView from the currently loaded
 * image's data sources. Pure function — does NOT touch the DOM.
 *
 * IMPORTANT: the returned object intentionally does NOT include the raw
 * piexif payload or libraw struct. Those carry PII (serials, owner name,
 * host computer, maker notes) that we never want callers to be able to
 * serialise. See Codex P9 review STOP-1 in CHANGELOG.
 *
 * MetadataView shape (all human-readable strings or null):
 *   {
 *     filename, format, dimensions, fileSize,
 *     colorSpace, bitDepth,
 *     camera: { make, model, lens, iso, shutter, aperture,
 *               focalLength, exposureBias, flash, whiteBalance },
 *     timestamp: { taken, modified, gpsTime },
 *     gps: { lat, lon, alt } | null,   // privacy-gated render in DOM
 *     ai: { vendor, model, sampler, steps, seed, cfgScale, prompt } | null
 *   }
 *
 * @param {object} ctx              { decodedMetadata?, blob?, aiMetadata?, w?, h?, fileName?, fileSize? }
 * @returns {Promise<object>}
 */
export async function buildMetadata(ctx = {}) {
    const safe = ctx || {};
    const fromExif = await extractFromExif(safe.blob);
    const fromRaw  = extractFromRawDecoded(safe.decodedMetadata);
    const dims = computeDimensions(safe);

    /* Merge order: RAW first (richest), then EXIF (fallback for fields
     * libraw doesn't surface or that the user-visible JPEG carries
     * differently), then AI metadata is its own section. */
    const camera = mergeCamera(fromRaw.camera, fromExif.camera);
    const timestamp = mergeTimestamp(fromRaw.timestamp, fromExif.timestamp);
    const gps = fromExif.gps || fromRaw.gps || null;

    /* Privacy invariant — Codex P9 review STOP-1: the PUBLIC return
     * value MUST NOT carry the raw piexif payload or libraw struct.
     * Those structures contain BodySerialNumber, LensSerialNumber,
     * CameraOwnerName, HostComputer, CameraSerialNumber, maker notes,
     * GPS coords, etc. The UI never rendered them, but a caller of
     * BlrrdUI.buildMetadata could still serialise the whole tree (e.g.
     * to a debug log or future "export metadata" feature) and leak PII.
     * We return a sanitized, normalized view only; the per-section
     * extractors keep their `raw` references LOCAL to this module. */
    return {
        filename:    safe.fileName || DASH,
        format:      detectFormat(safe.blob, safe.fileName),
        dimensions:  dims,
        fileSize:    formatSize(safe.fileSize),
        colorSpace:  fromExif.colorSpace || fromRaw.colorSpace || DASH,
        bitDepth:    fromRaw.bitDepth || fromExif.bitDepth || DASH,
        camera,
        timestamp,
        gps,
        ai:          normalizeAI(safe.aiMetadata),
    };
}

/**
 * Open the metadata modal. Idempotent — second call refreshes the body.
 * Reads from `window.E` (the legacy editor's central state) since this
 * is the same bridge other ESM seams use (window.BlrrdAI, etc).
 */
export async function openMetadataPanel() {
    if (!dialog) dialog = build();
    /* Refresh body from current state every open (cheap; <50ms even with
     * piexif EXIF parse for a 30 MP JPEG). */
    await refreshBody();
    if (!dialog.open) dialog.showModal();
}

/* ── Internal: DOM ──────────────────────────────────────────────────── */

function build() {
    const dlg = document.createElement('dialog');
    dlg.id = 'metadataModal';
    dlg.className = 'blrrd-meta-modal';
    /* Header + body skeleton; the body is populated by refreshBody(). */
    const header = el('div', { class: 'bmd-header' }, [
        el('span', { class: 'bmd-title', text: t('meta.title') || 'Image info' }),
        el('span', { class: 'bmd-subtitle', text: t('meta.subtitle') || 'read-only' }),
        el('button', {
            class: 'bmd-close',
            type: 'button',
            'aria-label': t('library.close') || 'Close',
            text: '×',
            onclick: () => closeDialog(),
        }),
    ]);
    const body = el('div', { class: 'bmd-body', id: 'bmdBody' });
    const inner = el('div', { class: 'bmd-inner' }, [header, body]);
    dlg.appendChild(inner);
    /* ESC / backdrop close. */
    dlg.addEventListener('cancel', (e) => { e.preventDefault(); closeDialog(); });
    dlg.addEventListener('click', (ev) => {
        /* Click outside .bmd-inner closes — backdrop click. */
        if (ev.target === dlg) closeDialog();
    });
    document.body.appendChild(dlg);
    return dlg;
}

function closeDialog() {
    if (dialog && dialog.open) dialog.close();
}

async function refreshBody() {
    if (!dialog) return;
    const body = dialog.querySelector('#bmdBody');
    if (!body) return;
    body.replaceChildren();

    const E = (typeof window !== 'undefined') ? window.E : null;
    /* Codex P9 review STOP-2: while a new image is decoding, E.fileName
     * + E.exifSourceBlob are already the NEW file's, but E.imgKey + E.w
     * + E.h still point at the OLD bitmap. Refuse to render — the panel
     * would otherwise mix metadata from two images. */
    const isLoading = !!(E && E.isLoadingImage);
    const hasImage = !!(E && (E.imgData || E.w));
    if (isLoading || !hasImage) {
        const text = isLoading
            ? (t('meta.loading') || 'Loading image…')
            : (t('meta.noImage') || 'No image loaded.');
        body.appendChild(el('div', { class: 'bmd-empty', text }));
        return;
    }

    const blob = (E && E.exifSourceBlob) || null;
    const ctx = {
        decodedMetadata: E && E.decodedMetadata,
        blob,
        aiMetadata: E && E.aiMetadata,
        w: E && E.w,
        h: E && E.h,
        fileName: E && E.fileName,
        fileSize: blob ? blob.size : null,
    };

    let view;
    try {
        view = await buildMetadata(ctx);
    } catch (err) {
        body.appendChild(el('div', {
            class: 'bmd-empty',
            text: (t('meta.error') || 'Failed to read metadata: ') + (err.message || err),
        }));
        return;
    }

    /* ── File section ─────────────────────────────────────────────── */
    body.appendChild(makeSection(t('meta.section.file') || 'File', [
        kv('meta.field.filename',   'Filename',    view.filename),
        kv('meta.field.format',     'Format',      view.format),
        kv('meta.field.dimensions', 'Dimensions',  view.dimensions),
        kv('meta.field.fileSize',   'File size',   view.fileSize),
        kv('meta.field.colorSpace', 'Color space', view.colorSpace),
        kv('meta.field.bitDepth',   'Bit depth',   view.bitDepth),
    ]));

    /* ── Camera section ───────────────────────────────────────────── */
    if (hasAnyValue(view.camera)) {
        body.appendChild(makeSection(t('meta.section.camera') || 'Camera', [
            kv('meta.field.make',         'Make',           view.camera.make),
            kv('meta.field.model',        'Model',          view.camera.model),
            kv('meta.field.lens',         'Lens',           view.camera.lens),
            kv('meta.field.iso',          'ISO',            view.camera.iso),
            kv('meta.field.shutter',      'Shutter speed',  view.camera.shutter),
            kv('meta.field.aperture',     'Aperture',       view.camera.aperture),
            kv('meta.field.focalLength',  'Focal length',   view.camera.focalLength),
            kv('meta.field.exposureBias', 'Exposure bias',  view.camera.exposureBias),
            kv('meta.field.flash',        'Flash',          view.camera.flash),
            kv('meta.field.whiteBalance', 'White balance',  view.camera.whiteBalance),
        ]));
    }

    /* ── Timestamps section ───────────────────────────────────────── */
    if (hasAnyValue(view.timestamp)) {
        body.appendChild(makeSection(t('meta.section.timestamp') || 'Timestamps', [
            kv('meta.field.taken',    'Date taken',    view.timestamp.taken),
            kv('meta.field.modified', 'Date modified', view.timestamp.modified),
            kv('meta.field.gpsTime',  'GPS time',      view.timestamp.gpsTime),
        ]));
    }

    /* ── GPS section (privacy-gated) ──────────────────────────────── */
    if (view.gps) {
        body.appendChild(buildGPSSection(view.gps));
    }

    /* ── AI metadata section ──────────────────────────────────────── */
    if (view.ai) {
        body.appendChild(buildAISection(view.ai));
    }
}

/* ── Section / row builders ─────────────────────────────────────────── */

function makeSection(title, rows) {
    const section = el('div', { class: 'bmd-section' });
    section.appendChild(el('div', { class: 'bmd-section-title', text: title }));
    const grid = el('div', { class: 'bmd-grid' });
    let visible = 0;
    for (const r of rows) {
        if (!r) continue;
        if (r.value === DASH || r.value == null || r.value === '') continue;
        grid.appendChild(r.node);
        visible++;
    }
    /* When all rows are empty (e.g. user closed without loading) we still
     * want to show the section title with a single em-dash so the user
     * knows what was missing. */
    if (visible === 0) {
        grid.appendChild(el('div', {
            class: 'bmd-grid-empty',
            text: DASH,
        }));
    }
    section.appendChild(grid);
    return section;
}

function kv(i18nKey, fallbackLabel, value) {
    const label = t(i18nKey) || fallbackLabel;
    const valStr = value == null ? DASH : String(value);
    const cell = el('div', { class: 'bmd-cell' }, [
        el('div', { class: 'bmd-key',   text: label }),
        el('div', { class: 'bmd-val',   text: valStr }),
    ]);
    return { node: cell, value: valStr };
}

function buildGPSSection(gps) {
    const section = el('div', { class: 'bmd-section bmd-section-gps' });
    section.appendChild(el('div', { class: 'bmd-section-title', text: t('meta.section.gps') || 'Location (GPS)' }));
    /* Privacy gate: hidden by default, opt-in via button. */
    const E = (typeof window !== 'undefined') ? window.E : null;
    const allowed = !!(E && E.metadataShowGPS === true);
    if (!allowed) {
        const warning = el('div', {
            class: 'bmd-gps-warning',
            text: t('meta.gpsWarning')
                || 'GPS coordinates are hidden by default to protect your privacy. Reveal only on devices you trust.',
        });
        const btn = el('button', {
            class: 'bmd-gps-reveal',
            type: 'button',
            text: t('meta.gpsReveal') || 'Show GPS coordinates',
        });
        btn.addEventListener('click', () => {
            if (typeof window !== 'undefined' && window.E) {
                window.E.metadataShowGPS = true;
            }
            /* Re-render section in place. */
            const fresh = buildGPSSection(gps);
            section.replaceWith(fresh);
        });
        section.appendChild(warning);
        section.appendChild(btn);
        return section;
    }
    /* User opted in — show coords. */
    const grid = el('div', { class: 'bmd-grid' });
    if (gps.lat != null) {
        grid.appendChild(kv('meta.field.gpsLat', 'Latitude',  formatLatLon(gps.lat, 'lat')).node);
    }
    if (gps.lon != null) {
        grid.appendChild(kv('meta.field.gpsLon', 'Longitude', formatLatLon(gps.lon, 'lon')).node);
    }
    if (gps.alt != null) {
        grid.appendChild(kv('meta.field.gpsAlt', 'Altitude',  gps.alt.toFixed(1) + ' m').node);
    }
    section.appendChild(grid);
    return section;
}

function buildAISection(ai) {
    const section = el('div', { class: 'bmd-section bmd-section-ai' });
    section.appendChild(el('div', { class: 'bmd-section-title', text: t('meta.section.ai') || 'AI generation metadata' }));
    const grid = el('div', { class: 'bmd-grid' });
    if (ai.vendor)  grid.appendChild(kv('meta.field.aiVendor',  'Generator', ai.vendor).node);
    if (ai.model)   grid.appendChild(kv('meta.field.aiModel',   'Model',     ai.model).node);
    if (ai.sampler) grid.appendChild(kv('meta.field.aiSampler', 'Sampler',   ai.sampler).node);
    if (ai.steps != null)    grid.appendChild(kv('meta.field.aiSteps', 'Steps', String(ai.steps)).node);
    if (ai.seed != null)     grid.appendChild(kv('meta.field.aiSeed',  'Seed',  String(ai.seed)).node);
    if (ai.cfgScale != null) grid.appendChild(kv('meta.field.aiCfg',   'CFG',   String(ai.cfgScale)).node);
    section.appendChild(grid);
    if (ai.prompt) {
        section.appendChild(el('div', { class: 'bmd-ai-prompt-label', text: t('ai.prompt') || 'Prompt' }));
        section.appendChild(el('div', { class: 'bmd-ai-prompt', text: String(ai.prompt) }));
    }
    return section;
}

/* ── Extractors ─────────────────────────────────────────────────────── */

function extractFromRawDecoded(meta) {
    const empty = {
        camera: emptyCamera(),
        timestamp: emptyTimestamp(),
        gps: null,
        colorSpace: null,
        bitDepth: null,
    };
    if (!meta || typeof meta !== 'object') return empty;
    /* libraw-wasm returns a flat-ish object. Field names vary by version,
     * so we probe several plausible keys. */
    const get = (...keys) => {
        for (const k of keys) {
            const v = readPath(meta, k);
            if (v != null && v !== '') return v;
        }
        return null;
    };
    const camera = {
        make:         normaliseStr(get('make', 'camera_make', 'Make')),
        model:        normaliseStr(get('model', 'camera_model', 'Model', 'normalized_model')),
        lens:         normaliseStr(get('lens', 'lens_model', 'lens_make', 'Lens')),
        iso:          formatInt(get('iso_speed', 'iso', 'ISO', 'ISOSpeedRatings')),
        shutter:      formatShutter(get('shutter', 'shutter_speed', 'ExposureTime')),
        aperture:     formatAperture(get('aperture', 'fnumber', 'FNumber')),
        focalLength:  formatFocal(get('focal_len', 'focal_length', 'FocalLength')),
        exposureBias: formatBias(get('exposure_bias', 'ExposureBiasValue')),
        flash:        DASH,
        whiteBalance: DASH,
    };
    const timestamp = {
        taken:    formatTimestamp(get('timestamp', 'datetime', 'DateTimeOriginal')),
        modified: formatTimestamp(get('datetime_modified', 'DateTime')),
        gpsTime:  null,
    };
    const bitDepth = (typeof meta.bitDepth === 'number') ? meta.bitDepth + ' bit'
                   : (typeof meta.bps === 'number') ? meta.bps + ' bit'
                   : null;
    return {
        camera,
        timestamp,
        gps: null, /* libraw exposes gps_lat/gps_long but they're rarely
                    * filled for camera RAW; keep null until we have a
                    * fixture to test against. */
        colorSpace: null,
        bitDepth,
    };
}

async function extractFromExif(blob) {
    const empty = {
        camera: emptyCamera(),
        timestamp: emptyTimestamp(),
        gps: null,
        colorSpace: null,
        bitDepth: null,
    };
    if (!blob || typeof blob.arrayBuffer !== 'function') return empty;
    const piexif = (typeof globalThis !== 'undefined') ? globalThis.piexif : null;
    if (!piexif || typeof piexif.load !== 'function') return empty;
    /* piexifjs only handles JPEG. HEIC/HEIF EXIF is reachable but the
     * API surface differs; out of scope for v1. */
    const mime = blob.type || '';
    if (!/jpe?g/i.test(mime)) return empty;

    let data;
    try {
        const dataURL = await blobToDataURL(blob);
        if (!dataURL) return empty;
        data = piexif.load(dataURL);
    } catch (_e) {
        return empty;
    }
    if (!data) return empty;

    const Tag = piexif.ImageIFD || {};
    const ETag = piexif.ExifIFD || {};
    const GTag = piexif.GPSIFD || {};
    const z = data['0th'] || {};
    const e = data['Exif'] || {};
    const g = data['GPS'] || {};

    /* Note: We intentionally do NOT read BodySerialNumber, LensSerialNumber,
     * CameraOwnerName, or HostComputer. These are PII fingerprinting
     * vectors per docs/THREAT-MODEL.md. */
    const camera = {
        make:         normaliseStr(z[Tag.Make]),
        model:        normaliseStr(z[Tag.Model]),
        lens:         normaliseStr(e[ETag.LensModel]) || normaliseStr(e[ETag.LensMake]),
        iso:          formatInt(takeFirst(e[ETag.ISOSpeedRatings])),
        shutter:      formatShutter(rationalToNumber(e[ETag.ExposureTime])),
        aperture:     formatAperture(rationalToNumber(e[ETag.FNumber])),
        focalLength:  formatFocal(rationalToNumber(e[ETag.FocalLength])),
        exposureBias: formatBias(rationalToNumber(e[ETag.ExposureBiasValue])),
        flash:        formatFlash(e[ETag.Flash]),
        whiteBalance: formatWB(e[ETag.WhiteBalance]),
    };
    const timestamp = {
        taken:    normaliseStr(e[ETag.DateTimeOriginal]) || normaliseStr(z[Tag.DateTime]),
        modified: normaliseStr(z[Tag.DateTime]),
        gpsTime:  formatGPSTime(g[GTag.GPSDateStamp], g[GTag.GPSTimeStamp]),
    };
    const colorSpace = formatColorSpace(e[ETag.ColorSpace]);
    const bitDepth = formatBitDepth(z[Tag.BitsPerSample]);
    const gps = extractGPSFromExif(g, GTag);
    return { camera, timestamp, gps, colorSpace, bitDepth };
}

function extractGPSFromExif(g, GTag) {
    if (!g || !GTag || !GTag.GPSLatitude) return null;
    const latRaw = g[GTag.GPSLatitude];
    const lonRaw = g[GTag.GPSLongitude];
    if (!latRaw || !lonRaw) return null;
    const latRef = g[GTag.GPSLatitudeRef]  || 'N';
    const lonRef = g[GTag.GPSLongitudeRef] || 'E';
    const lat = dmsToDecimal(latRaw, latRef);
    const lon = dmsToDecimal(lonRaw, lonRef);
    if (lat == null || lon == null) return null;
    let alt = null;
    const altRaw = g[GTag.GPSAltitude];
    if (altRaw) {
        const altVal = rationalToNumber(altRaw);
        if (Number.isFinite(altVal)) {
            const altRef = g[GTag.GPSAltitudeRef];
            alt = (altRef === 1) ? -altVal : altVal;
        }
    }
    return { lat, lon, alt };
}

function dmsToDecimal(dms, ref) {
    if (!Array.isArray(dms) || dms.length < 3) return null;
    const d = rationalToNumber(dms[0]);
    const m = rationalToNumber(dms[1]);
    const s = rationalToNumber(dms[2]);
    if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(s)) return null;
    let dec = d + m / 60 + s / 3600;
    if (ref === 'S' || ref === 'W') dec = -dec;
    return dec;
}

function normalizeAI(meta) {
    if (!meta || !meta.present) return null;
    return {
        vendor:   meta.vendor || 'unknown',
        model:    meta.model || null,
        sampler:  meta.sampler || null,
        steps:    meta.steps != null ? meta.steps : null,
        seed:     meta.seed != null ? meta.seed : null,
        cfgScale: meta.cfgScale != null ? meta.cfgScale : null,
        prompt:   meta.prompt || null,
    };
}

/* ── Mergers ────────────────────────────────────────────────────────── */

function mergeCamera(primary, fallback) {
    const out = emptyCamera();
    for (const k of Object.keys(out)) {
        out[k] = pick(primary && primary[k], fallback && fallback[k]) || DASH;
    }
    return out;
}

function mergeTimestamp(primary, fallback) {
    const out = emptyTimestamp();
    for (const k of Object.keys(out)) {
        out[k] = pick(primary && primary[k], fallback && fallback[k]) || DASH;
    }
    return out;
}

function pick(a, b) {
    if (a != null && a !== '' && a !== DASH) return a;
    if (b != null && b !== '' && b !== DASH) return b;
    return null;
}

function emptyCamera() {
    return {
        make: DASH, model: DASH, lens: DASH,
        iso: DASH, shutter: DASH, aperture: DASH,
        focalLength: DASH, exposureBias: DASH,
        flash: DASH, whiteBalance: DASH,
    };
}

function emptyTimestamp() {
    return { taken: DASH, modified: DASH, gpsTime: DASH };
}

function hasAnyValue(obj) {
    if (!obj) return false;
    for (const k of Object.keys(obj)) {
        if (obj[k] != null && obj[k] !== '' && obj[k] !== DASH) return true;
    }
    return false;
}

/* ── Formatters ─────────────────────────────────────────────────────── */

function formatInt(v) {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return String(Math.round(n));
}

function formatShutter(v) {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (n >= 1) return n.toFixed(1) + ' s';
    /* Reciprocal form: 1/250 s. */
    const denom = Math.round(1 / n);
    if (denom > 0) return '1/' + denom + ' s';
    return n.toFixed(4) + ' s';
}

function formatAperture(v) {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    return 'f/' + n.toFixed(1);
}

function formatFocal(v) {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n.toFixed(1) + ' mm';
}

function formatBias(v) {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    if (n === 0) return '0 EV';
    return (n > 0 ? '+' : '') + n.toFixed(2) + ' EV';
}

function formatFlash(v) {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    /* Bit 0 = flash fired. */
    return (n & 1) ? (t('meta.flash.fired')   || 'Fired')
                   : (t('meta.flash.noFire') || 'Did not fire');
}

function formatWB(v) {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return n === 0 ? (t('meta.wb.auto')   || 'Auto')
                   : (t('meta.wb.manual') || 'Manual');
}

function formatColorSpace(v) {
    if (v == null) return null;
    const n = Number(v);
    if (n === 1) return 'sRGB';
    if (n === 65535) return 'Uncalibrated';
    return null;
}

function formatBitDepth(v) {
    if (v == null) return null;
    if (Array.isArray(v)) {
        if (v.every((x) => x === v[0])) return v[0] + ' bit';
        return v.join('/') + ' bit';
    }
    if (typeof v === 'number') return v + ' bit';
    return null;
}

function formatTimestamp(v) {
    if (v == null) return null;
    /* libraw-wasm wraps timestamp into a JS Date object (per
     * public/vendor/libraw/index.js). Codex P9 review STOP-3: previous
     * version handled only number/string and silently dropped RAW
     * timestamps. */
    if (v instanceof Date && !isNaN(v.getTime())) {
        return v.toISOString().replace('T', ' ').slice(0, 19);
    }
    /* Some libraw bindings emit unix seconds instead of Date. */
    if (typeof v === 'number' && Number.isFinite(v)) {
        try {
            const d = new Date(v * 1000);
            if (!isNaN(d.getTime())) return d.toISOString().replace('T', ' ').slice(0, 19);
        } catch (_e) { /* fall through */ }
    }
    /* EXIF timestamps are already "YYYY:MM:DD HH:MM:SS" strings. */
    if (typeof v === 'string' && v.length) return v;
    return null;
}

function formatGPSTime(dateStamp, timeStamp) {
    if (!dateStamp && !timeStamp) return null;
    let out = '';
    if (typeof dateStamp === 'string') out += dateStamp;
    if (Array.isArray(timeStamp) && timeStamp.length === 3) {
        const h = rationalToNumber(timeStamp[0]);
        const m = rationalToNumber(timeStamp[1]);
        const s = rationalToNumber(timeStamp[2]);
        if (Number.isFinite(h) && Number.isFinite(m) && Number.isFinite(s)) {
            out += ' ' + pad2(h) + ':' + pad2(m) + ':' + pad2(s) + ' UTC';
        }
    }
    return out.trim() || null;
}

function pad2(n) { const s = String(Math.floor(n)); return s.length < 2 ? '0' + s : s; }

function formatLatLon(v, kind) {
    if (!Number.isFinite(v)) return DASH;
    const abs = Math.abs(v);
    const deg = Math.floor(abs);
    const minF = (abs - deg) * 60;
    const min = Math.floor(minF);
    const sec = ((minF - min) * 60).toFixed(2);
    const hemi = kind === 'lat'
        ? (v >= 0 ? 'N' : 'S')
        : (v >= 0 ? 'E' : 'W');
    return v.toFixed(6) + '° (' + deg + '° ' + min + "' " + sec + '" ' + hemi + ')';
}

function formatSize(bytes) {
    if (bytes == null || !Number.isFinite(bytes)) return DASH;
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
    return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}

function detectFormat(blob, fileName) {
    if (blob && blob.type) {
        const t2 = String(blob.type).toLowerCase();
        if (t2.includes('jpeg') || t2.includes('jpg')) return 'JPEG';
        if (t2.includes('png'))   return 'PNG';
        if (t2.includes('webp'))  return 'WebP';
        if (t2.includes('avif'))  return 'AVIF';
        if (t2.includes('gif'))   return 'GIF';
        if (t2.includes('bmp'))   return 'BMP';
        if (t2.includes('tiff'))  return 'TIFF';
        if (t2.includes('heic') || t2.includes('heif')) return 'HEIC';
    }
    if (typeof fileName === 'string') {
        const m = /\.([a-z0-9]+)$/i.exec(fileName);
        if (m) {
            const ext = m[1].toUpperCase();
            const map = {
                JPG: 'JPEG', JPEG: 'JPEG', PNG: 'PNG', WEBP: 'WebP', AVIF: 'AVIF',
                GIF: 'GIF', BMP: 'BMP', TIF: 'TIFF', TIFF: 'TIFF',
                HEIC: 'HEIC', HEIF: 'HEIC', HIF: 'HEIC',
                EXR: 'EXR', DPX: 'DPX',
                CR2: 'RAW (CR2)', CR3: 'RAW (CR3)', NEF: 'RAW (NEF)',
                ARW: 'RAW (ARW)', DNG: 'RAW (DNG)', RAF: 'RAW (RAF)',
                RW2: 'RAW (RW2)', ORF: 'RAW (ORF)', X3F: 'RAW (X3F)',
            };
            return map[ext] || ext;
        }
    }
    return DASH;
}

function computeDimensions(ctx) {
    const w = (ctx && ctx.w) || null;
    const h = (ctx && ctx.h) || null;
    if (w && h) {
        const mp = (w * h / 1_000_000).toFixed(1);
        return w + ' × ' + h + ' (' + mp + ' MP)';
    }
    return DASH;
}

/* ── Util ───────────────────────────────────────────────────────────── */

function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
        if (k === 'class') node.className = v;
        else if (k === 'text') node.textContent = v;
        else if (k.startsWith('on') && typeof v === 'function') {
            node.addEventListener(k.slice(2), v);
        } else if (typeof v === 'boolean') {
            if (v) node.setAttribute(k, '');
        } else if (v != null) node.setAttribute(k, v);
    }
    for (const child of [].concat(children || [])) {
        if (child == null) continue;
        node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
    }
    return node;
}

function readPath(obj, path) {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((a, k) => (a == null ? a : a[k]), obj);
}

function rationalToNumber(v) {
    if (Array.isArray(v) && v.length === 2 && v[1] !== 0) return v[0] / v[1];
    if (typeof v === 'number') return v;
    return NaN;
}

function takeFirst(v) {
    if (Array.isArray(v)) return v.length ? v[0] : null;
    return v;
}

function normaliseStr(v) {
    if (v == null) return null;
    if (typeof v !== 'string') v = String(v);
    /* Trim NUL terminators + whitespace; piexifjs leaves trailing \0. */
    const cleaned = v.replace(/ +$/, '').trim();
    return cleaned || null;
}

async function blobToDataURL(blob) {
    if (!blob) return null;
    const buf = await blob.arrayBuffer();
    const u8 = new Uint8Array(buf);
    let s = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < u8.length; i += CHUNK) {
        const end = Math.min(i + CHUNK, u8.length);
        s += String.fromCharCode.apply(null, u8.subarray(i, end));
    }
    const b64 = (typeof btoa === 'function') ? btoa(s)
              : (globalThis.btoa ? globalThis.btoa(s) : null);
    if (b64 == null) return null;
    const mime = blob.type || 'image/jpeg';
    return 'data:' + mime + ';base64,' + b64;
}
